import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Truck, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { API } from '../App';
import { useUIPreview } from '../contexts/UIPreviewContext';
import { useActivityBar } from '../contexts/ActivityBarContext';

const ORANGE = '#ee7203';

/**
 * Shown after dropping an Unplanned Queue pill onto a vehicle box.
 * Lets the user choose which run to plan it into, and — if that run already
 * has an entry with a matching postcode — offers to merge as a 2nd order
 * number under the existing entry instead of creating a new line.
 */
const PlanOrderModal = ({ open, onClose, item, vehicle, onPlanned, sourceUi = 'unknown', presetRunId = null }) => {
  const { modalUiV2 } = useUIPreview();
  const activityBar = useActivityBar();
  const [runChoice, setRunChoice] = useState(null);
  const [mergeCandidate, setMergeCandidate] = useState(null); // entry object or null
  const [confirmingMerge, setConfirmingMerge] = useState(false);
  const [saving, setSaving] = useState(false);

  // Computed defensively (item/vehicle may briefly be null between renders) so hook order below
  // stays stable — the "nothing to show yet" early return happens AFTER all hooks are declared.
  const runs = item && vehicle ? [
    { key: 'main', label: 'Run 1', entries: vehicle.entries || [] },
    ...((vehicle.runs || []).map((r, i) => ({ key: r.id, label: `Run ${r.run_number || i + 2}`, entries: r.entries || [] }))),
    { key: 'new', label: '+ New Run', entries: [] },
  ] : [];

  const selectRun = (run) => {
    if (run.key !== 'new') {
      const match = run.entries.find(e => (e.postcode || '').trim().toUpperCase() === (item.postcode || '').trim().toUpperCase());
      if (match) {
        setMergeCandidate(match);
        setConfirmingMerge(true);
        setRunChoice(run.key);
        return;
      }
    }
    setRunChoice(run.key);
    doPlan(run.key, null);
  };

  const doPlan = async (chosenRun, mergeEntryId) => {
    setSaving(true);
    try {
      await api.post(`${API}/transport-planning/plan`, {
        unplanned_id: item.id,
        vehicle_id: vehicle.id,
        run_choice: chosenRun,
        merge_entry_id: mergeEntryId,
        source_ui: sourceUi,
      });
      toast.success(mergeEntryId ? 'Added as a second order to the existing delivery' : 'Order planned onto the board');
      onPlanned();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to plan order');
    } finally {
      setSaving(false);
      setConfirmingMerge(false);
    }
  };

  // Dropped directly onto a specific run's drop zone / entry card (see TransportNewUI's
  // handleUnplannedDragEnd) — skip straight to that run the moment the modal opens, same as
  // clicking it manually, instead of always making the user re-pick the run they just dropped on.
  useEffect(() => {
    if (!open || !presetRunId || !item || !vehicle) return;
    const match = runs.find((r) => r.key === presetRunId);
    if (match) selectRun(match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, presetRunId, item, vehicle]);

  if (!item || !vehicle) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="plan-order-modal" {...(modalUiV2 ? { topOffset: activityBar.headerHeight, overlayClassName: 'bg-black/50' } : {})}>
        {confirmingMerge ? (
          <>
            <DialogHeader>
              <DialogTitle>Add to existing order?</DialogTitle>
              <DialogDescription>
                <strong>{mergeCandidate?.customer_name}</strong> at <strong>{mergeCandidate?.postcode}</strong> is
                already on this run. Attach order <strong>{item.order_number || 'this pill'}</strong> as a second
                order number on that same delivery?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="outline" data-testid="plan-merge-no-btn" onClick={() => doPlan(runChoice, null)} disabled={saving}>
                No, add as new line
              </Button>
              <Button data-testid="plan-merge-yes-btn" onClick={() => doPlan(runChoice, mergeCandidate.id)} disabled={saving}
                style={{ backgroundColor: ORANGE, color: 'white' }}>
                Yes, add to existing order
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5" style={{ color: ORANGE }} /> Plan onto {vehicle.vehicle_registration}
              </DialogTitle>
              <DialogDescription className="flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" /> {item.customer_name} — {item.postcode} {item.order_number && `(#${item.order_number})`}
              </DialogDescription>
            </DialogHeader>
            <p className="text-xs text-gray-500 -mt-2">Choose which run to add this delivery to:</p>
            <div className="space-y-2 py-1">
              {runs.map((run) => (
                <button
                  key={run.key}
                  data-testid={`plan-run-choice-${run.key}`}
                  disabled={saving}
                  onClick={() => selectRun(run)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 hover:shadow-md transition-all text-left disabled:opacity-50"
                  style={{ borderColor: run.key === 'new' ? '#d1d5db' : ORANGE, backgroundColor: run.key === 'new' ? '#f9fafb' : '#fff7ed' }}
                >
                  <span className="font-semibold text-sm text-gray-800">{run.label}</span>
                  <span className="text-xs text-gray-500">{run.entries.length} stop{run.entries.length !== 1 ? 's' : ''}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PlanOrderModal;
